import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

const THRESHOLD = 72; // px of (damped) pull that triggers a refresh

/**
 * Pull-to-refresh for the installed PWA: standalone mode has no browser reload,
 * so dragging down from the top of a tab reloads the app — which also picks up
 * the newest deployed version (the service worker fetches navigations
 * network-first). Renders as the app's scroll container.
 */
export default function PullToRefresh({ children }: { children: ReactNode }) {
  const elRef = useRef<HTMLElement>(null);
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();

  // The four tabs share this one scroll container — reset it on every route
  // change so a screen never inherits the previous screen's scroll offset
  // (e.g. backing out of a chat used to leave Profile shifted into the notch).
  // ALSO reset the window itself: when the chat keyboard opens, iOS standalone
  // scrolls the document to reveal the input and sometimes never scrolls back,
  // which shifts every screen up into the camera space.
  useEffect(() => {
    elRef.current?.scrollTo(0, 0);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Belt and braces: whenever the on-screen keyboard closes anywhere in the
  // app, snap the document back to the top.
  useEffect(() => {
    const onFocusOut = () => {
      setTimeout(() => {
        if (window.scrollY !== 0) window.scrollTo(0, 0);
        if (document.documentElement.scrollTop !== 0) document.documentElement.scrollTop = 0;
      }, 60);
    };
    document.addEventListener("focusout", onFocusOut);
    return () => document.removeEventListener("focusout", onFocusOut);
  }, []);

  function onTouchStart(e: React.TouchEvent) {
    // Never engage inside overlays (profile sheets, bottom sheets) or immersive
    // screens (chat, live games): their scrolling must not become a reload.
    if ((e.target as HTMLElement).closest(".detail-backdrop, .sheet-backdrop, .chat-screen")) {
      startY.current = null;
      return;
    }
    const el = elRef.current;
    startY.current = el && el.scrollTop <= 0 ? e.touches[0].clientY : null;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (startY.current == null || refreshing) return;
    const el = elRef.current;
    if (!el || el.scrollTop > 0) { startY.current = null; setPull(0); return; }
    const dy = e.touches[0].clientY - startY.current;
    // Damped drag, capped — feels like the native rubber band.
    setPull(dy > 0 ? Math.min(dy * 0.45, 110) : 0);
  }

  function onTouchEnd() {
    if (refreshing) return;
    if (pull >= THRESHOLD) {
      setRefreshing(true);
      setPull(56); // hold the indicator visible while reloading
      window.location.reload();
    } else {
      setPull(0);
    }
    startY.current = null;
  }

  return (
    <main
      className="app-main"
      ref={elRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="ptr-indicator"
        style={{ height: pull, opacity: Math.min(1, pull / THRESHOLD) }}
        aria-hidden={pull === 0}
      >
        <div
          className="spinner"
          style={{
            width: 22, height: 22, borderWidth: 2.5,
            animation: refreshing || pull >= THRESHOLD ? undefined : "none",
            transform: refreshing ? undefined : `rotate(${pull * 3}deg)`,
          }}
        />
        <span className="ptr-label">
          {refreshing ? "Refreshing…" : pull >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}
        </span>
      </div>
      {/* height:100% lets full-height screens (Discover's deck) size correctly. */}
      <div style={{ height: "100%" }}>{children}</div>
    </main>
  );
}
